<script setup lang="ts">
import { z } from 'zod'

import VRadio from '~/components/inputs/radio.vue'
import TagSelectFromType from '~/components/tag/selectFromType.vue'
import TagSelectFromTypeLoopOnParent from '~/components/tag/selectFromTypeLoopOnParent.vue'
import HeightExpand from '~/components/transitions/heightExpand.vue'

import { useFieldValidation } from '~/composables/useFieldValidation'
import { useSortTagsByTranslatedName } from '~/composables/useTagHelpers'

import { useTagStore } from '~/stores/tag'

import tagAvailableParentKeys from 'assets/json/tagAvailableParentKeys'

import type Tag from '~/entities/tag'
import type TagType from '~/entities/tagType'
import type { TagParentTypeKey } from '~/types/tagParentTypeKey'

type AllowedComponents =
  | typeof TagSelectFromType
  | typeof TagSelectFromTypeLoopOnParent

type Props = {
  modelValue: number[]
  typeName: TagParentTypeKey
  title: string
  readonly?: boolean
  maxAmount?: number
  pValidity?: boolean
}

type Emits = {
  'update:modelValue': [value: number[]]
  change: []
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  maxAmount: 0,
  pValidity: true,
})
const emit = defineEmits<Emits>()

enum ExclusivityEnum {
  all = '_all_',
  specific = '',
}

const sortTagsByTranslatedName = useSortTagsByTranslatedName()
const _uid = useId()
const { t } = useI18n()

const { GET_TAG_TYPE_FROM_NAME, GET_TAGS_FROM_IDS } = useTagStore()

const specificSelectionBackup = ref<number[]>([])
const isSpecificSelectionRadioAvailable = ref(false)
const fieldName = `toggle-field-${_uid}`
const { value: fieldValue } = useFieldValidation(
  fieldName,
  z
    .number()
    .array()
    .nonempty({
      message: t('tags.toggleAllAndType.errors.radioNotSelected'),
    }),
  {
    syncVModel: false,
    validateOnValueUpdate: false,
    initialValue: props.modelValue,
  },
)

const exclusivityOptions = computed(() => {
  return Object.values(ExclusivityEnum)
})

const tagType = computed<TagType | undefined>(() => {
  return GET_TAG_TYPE_FROM_NAME(props.typeName)
})

const availableTags = computed<Tag[]>(() => {
  return GET_TAGS_FROM_IDS(tagType.value?.tag_ids ?? []).sort((a, b) =>
    sortTagsByTranslatedName(props.typeName, a, b),
  )
})

const isSpecificRadioSelected = computed<boolean>(() => {
  return props.modelValue.length
    ? !isSpecialTagSelected(ExclusivityEnum.all)
    : isSpecificSelectionRadioAvailable.value
})

const isRadioSelected = computed<boolean>(() => {
  return (
    (props.modelValue.length
      ? isSpecialTagSelected(ExclusivityEnum.all)
      : false) || isSpecificRadioSelected.value
  )
})

const selectedComponent = computed<AllowedComponents>(() => {
  if (tagAvailableParentKeys.includes(props.typeName))
    return TagSelectFromTypeLoopOnParent
  else return TagSelectFromType
})

isSpecificSelectionRadioAvailable.value = isSpecialTagSelected(
  ExclusivityEnum.all,
)

watch(
  () => props.modelValue,
  (newVal) => {
    fieldValue.value = newVal
  },
)

function getSpecialTagFromOption(option: ExclusivityEnum) {
  return availableTags.value.find((tag: Tag): boolean =>
    isSpecialTag(option, tag.name),
  )
}

function isSpecialTagSelected(option: ExclusivityEnum) {
  if (getSpecialTagFromOption(option))
    return props.modelValue.includes(getSpecialTagFromOption(option)?.id ?? -1)
  else return false
}

function hasExclusivityOptionValue(option: ExclusivityEnum) {
  if (option !== ExclusivityEnum.specific)
    return props.modelValue.length ? isSpecialTagSelected(option) : false
  else return isSpecificRadioSelected.value
}

function backupSpecificSelection() {
  const specialTagIds = [getSpecialTagFromOption(ExclusivityEnum.all)?.id ?? -1]
  if (!specialTagIds.includes(props.modelValue[0])) {
    // Make sure we don't reset the last non special selection if this.modelValue = special only
    specificSelectionBackup.value = props.modelValue.filter(
      (tagId: number) => !specialTagIds.includes(tagId),
    )
  }
}

function retrieveBackup() {
  emit('update:modelValue', specificSelectionBackup.value)
  emit('change')
}

function handleRadioInput(option: ExclusivityEnum) {
  const selectSpecialTag = option !== ExclusivityEnum.specific
  if (selectSpecialTag && selectSpecialTag !== isSpecialTagSelected(option))
    setSpecialTagAsValue(option)
  else retrieveBackup()

  isSpecificSelectionRadioAvailable.value = true
}

const removeAllTagFromValue = (tagId: number) =>
  tagId !== (getSpecialTagFromOption(ExclusivityEnum.all)?.id ?? -1)

function handlePillSelection(value: number[]) {
  const filteredValue = value.filter(removeAllTagFromValue)

  if (!isSpecialTagSelected(ExclusivityEnum.specific)) {
    specificSelectionBackup.value = filteredValue
    handleRadioInput(ExclusivityEnum.specific)
  }

  emit('update:modelValue', filteredValue)
  emit('change')
}

function setSpecialTagAsValue(option: ExclusivityEnum) {
  const specificTagId = getSpecialTagFromOption(option)?.id ?? -1

  backupSpecificSelection()
  if (specificTagId !== -1) emit('update:modelValue', [specificTagId])
  else emit('update:modelValue', [])

  emit('change')
}

function isSpecialTag(
  option: ExclusivityEnum,
  tagName: Tag['name'] | ExclusivityEnum,
) {
  return tagName === option
}

function isSpecificTag(option: ExclusivityEnum): boolean {
  return option === ExclusivityEnum.specific
}
</script>

<template>
  <div class="toggleAllAndType">
    <div
      :id="_uid"
      class="tw-sans tw-color-black tw-font-sans tw-text-base tw-font-medium"
    >
      {{ title }}
    </div>
    <div class="tw-mt-4">
      <div class="tw-grid tw-gap-4">
        <div v-for="option in exclusivityOptions" :key="option">
          <VRadio
            v-if="isSpecificTag(option) || getSpecialTagFromOption(option)"
            :model-value="hasExclusivityOptionValue(option)"
            :label="$t(`tags.toggleAllAndType.label.${typeName}${option}`)"
            :p-validity="pValidity"
            :name="fieldName"
            @update:model-value="handleRadioInput(option)"
            @change="emit('change')"
          />
        </div>
      </div>
      <HeightExpand>
        <component
          :is="selectedComponent"
          v-if="isSpecificRadioSelected || typeName === 'lyrics_lang'"
          class="tw-mt-4"
          :type-name="typeName"
          :model-value="modelValue"
          :p-validity="pValidity"
          :include-special-tag="typeName === 'lyrics_lang' ? 'none' : undefined"
          @update:model-value="handlePillSelection"
        />
      </HeightExpand>
      <HeightExpand>
        <div
          v-if="!pValidity"
          key="radioIssue"
          class="tw-mt-4 tw-text-xs tw-font-medium tw-text-error"
        >
          <span v-if="isRadioSelected">{{
            $t(`tags.toggleAllAndType.errors.${typeName}`)
          }}</span>
          <span v-else>{{
            $t('tags.toggleAllAndType.errors.radioNotSelected')
          }}</span>
        </div>
      </HeightExpand>
    </div>
  </div>
</template>
